/**
 * One person the user meets with, as the team page lists them.
 *
 * Grown rather than kept by hand: `meeting_count` is how many conversations
 * with this person have already happened, and it never goes down. `label` is
 * what to call them — the best display name any invite ever carried for them,
 * the name the user typed, or the address when there is nothing else.
 *
 * One person, not one address. A company on several domains invites the same
 * colleague as `jan@acme.com` one week and `jan@acme.be` the next, and both are
 * this one item: the counts and dates are theirs together, `email` is the
 * address they are best known by, and `emails` is every address they turned up
 * under. A search box has to look at all of them — the address somebody types is
 * as likely to be the other one.
 *
 * A user without a calendar has no addresses to go on and tags who a meeting is
 * with by name. Those people are items just the same, with `email` null and
 * `emails` empty; `label` is the name that was typed.
 *
 * `last_meeting_at` and `next_meeting_at` are both nullable and mean different
 * kinds of nothing: no conversation has happened yet, and none is planned.
 */
export interface ColleagueListItem {
  id: string;
  /** Null for somebody known by name alone. */
  email: string | null;
  /**
   * Every address this person meets under, `email` included. Empty for
   * somebody known by name alone.
   */
  emails: string[];
  /** What to call them. Resolved by the backend, never empty. */
  label: string;
  /**
   * Their profile picture, when this colleague uses Wundo themselves and has
   * one. Null for everybody else, which is most of a team page — a colleague row
   * exists for anybody on the company's domains who turns up in the calendar,
   * and an account can only be found by address, never by a typed name.
   *
   * Never resolved across organisations: see UserProfileImagePolicy.
   */
  profile_image_url: string | null;
  meeting_count: number;
  last_meeting_at: string | null;
  next_meeting_at: string | null;
  /** Hidden from the overview by the user. The row keeps counting. */
  hidden: boolean;
}

/**
 * Who a 1:1 colleague is to the user: somebody they lead (the default), their
 * own manager, or neither.
 */
export const COLLEAGUE_RELATIONSHIPS = ['team_member', 'manager', 'other'] as const;
export type ColleagueRelationship = (typeof COLLEAGUE_RELATIONSHIPS)[number];

/**
 * One colleague on the Team page: somebody the user has a 1:1 with, from three
 * months back to one month ahead. `id` is their colleague row, what their
 * conversations are opened by. `emails` is every address they are known by,
 * their 1:1 thread's included; `email` is the one a follow is written on.
 */
export interface OneOnOneColleagueItem {
  id: string;
  email: string;
  emails: string[];
  label: string;
  profile_image_url: string | null;
  /** Their main memory exists and holds at least one fact. */
  memory: boolean;
  /** When their main memory last changed. Null while `memory` is false. */
  history_updated_at: string | null;
  /** The user asked Wundo to support their 1:1s with this person. */
  supported: boolean;
  /**
   * Whether the notetaker joins the 1:1s with them, as the decision falls: the
   * standing choice for this person when there is one, else the user's
   * general notetaker choice. It only joins while `supported` is on.
   */
  notetaker: boolean;
  /** Who they are to the user, as the user set it. */
  relationship: ColleagueRelationship;
  /** Their 1:1s from three months back to one month ahead, past and planned. */
  one_on_one_count: number;
  next_one_on_one_at: string | null;
  last_one_on_one_at: string | null;
}

/**
 * One 1:1 with a colleague, as their page lists it: a meeting Wundo takes to
 * be a 1:1 with them, or one attached to their 1:1 thread, whether or not
 * Wundo follows it. What the page says about it is what a reader wants before
 * opening it: is it prepared, will the notetaker be there, is it written up.
 */
export interface OneOnOneMeetingItem {
  id: string;
  title: string | null;
  starts_at: string;
  ends_at: string;
  /** Wundo follows this one: it prepares it and writes it up. */
  tracked: boolean;
  /** The notetaker joins. Only says something about a meeting still to come. */
  notetaker: boolean;
  /** A preparation exists. */
  has_prep: boolean;
  /** A verslag exists. */
  has_report: boolean;
}

/**
 * Where a fact came from. A meeting source names the meeting whose verslag it
 * came from, with when that meeting took place; a chat source is a talk with
 * Wundo and carries nothing more.
 */
export type OneOnOneMemorySource =
  | { kind: 'meeting'; meeting_id: string; starts_at: string }
  | { kind: 'chat' };

/** One thing Wundo remembers, with where it came from, oldest source first. */
export interface OneOnOneMemoryFact {
  text: string;
  sources: OneOnOneMemorySource[];
}

/**
 * What Wundo remembers of a colleague: their main memory, in its fixed shape.
 * `topics` carry the facts under a heading in the user's language; `open` is
 * what is still to come back to. Never the agent's own notes.
 */
export interface OneOnOneMemory {
  /** An old memory written as free text, from before the fixed shape. */
  prose: string | null;
  topics: { title: string; facts: OneOnOneMemoryFact[] }[];
  open: OneOnOneMemoryFact[];
  updated_at: string;
}

/**
 * How often the two see each other, read off the 1:1s themselves — the typical
 * gap between one and the next, past and planned alike — or, with fewer than
 * two to go on, when the one there is takes or took place.
 */
export type OneOnOneRhythm =
  | {
      kind: 'cadence';
      /** The median gap, rounded to a cadence people say: 1, 2, 3, 4 (a month), 8 (two months), or any other number of weeks. */
      weeks: number;
    }
  | { kind: 'first'; at: string };

/**
 * A colleague's page: who they are as the Team page lists them, and every 1:1
 * with them either side of now.
 */
export interface OneOnOneColleagueDetail {
  colleague: OneOnOneColleagueItem;
  /** Null when no 1:1 with them is left to read it from. */
  rhythm: OneOnOneRhythm | null;
  /** The living document of their 1:1 conversation, or null while there is none. */
  memory: OneOnOneMemory | null;
  /** Taken off the Team page by the user. */
  hidden: boolean;
  /** Soonest first: every 1:1 the calendar still holds with them. */
  upcoming: OneOnOneMeetingItem[];
  /** Newest first: every 1:1 with them that is over. */
  past: OneOnOneMeetingItem[];
}
